import React, { useCallback, useMemo } from 'react';
import { di } from 'react-magnetic-di';
import Toggle from '@atlaskit/toggle';
import { expVal } from '@atlassian/jira-feature-experiments';
import { withFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { gicSmartFieldsFeatureResource } from '@atlassian/jira-router-resources-gic-smart-fields/src/services/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { messages } from '../../common/messages.tsx';
import { useSmartFieldsFeedbackCollector } from '../../common/ui/feedback/index.tsx';
import { useSmartFieldsUpdateUserPreference } from '../../services/index.tsx';

export const useSmartFieldsPreferenceResource = () => useResource(gicSmartFieldsFeatureResource);

// TODO: Delete this file when cleaning up smart_fields_gic_toggle_fg
export const SmartFieldsPreference = withFlagService((props) => {
	di(window);
	const { loading, data, update: updateResource } = useSmartFieldsPreferenceResource();
	const { updatePreferences } = useSmartFieldsUpdateUserPreference();
	const { formatMessage } = useIntl();

	const {
		flagService: { showFlag },
	} = props;

	const hasUserOptedOut = useMemo(
		() => (typeof data?.hasUserOptedOut === 'boolean' ? data.hasUserOptedOut : false),
		[data],
	);

	const hasUserDismissedReadMore = useMemo(
		() =>
			typeof data?.hasUserDismissedReadMore === 'boolean' ? data.hasUserDismissedReadMore : false,
		[data],
	);
	const { openSmartFieldsFeedbackCollector } = useSmartFieldsFeedbackCollector();

	const successTitleOptingOutMessage = expVal(
		'issue-terminology-refresh-m2-replace',
		'isEnabled',
		false,
	)
		? messages.successTitleOptingOutIssueTermRefresh
		: messages.successTitleOptingOut;

	const successTitleOptingInMessage = expVal(
		'issue-terminology-refresh-m2-replace',
		'isEnabled',
		false,
	)
		? messages.successTitleOptingInIssueTermRefresh
		: messages.successTitleOptingIn;

	const onChange = useCallback(
		async (_: React.ChangeEvent<HTMLInputElement>, analyticsEventUI: UIAnalyticsEvent) => {
			const value = !hasUserOptedOut;
			updateResource(() => ({ hasUserOptedOut: value, hasUserDismissedReadMore: false }));

			fireUIAnalytics(analyticsEventUI, 'toggleLabsGicSmartFields', {
				isSmartFieldsOptOut: value,
			});

			try {
				await updatePreferences({
					hasUserOptedOut: value,
					hasUserDismissedReadMore: false,
				});
				showFlag({
					messageId: 'personal-settings-gic-smart-fields.ui.preference.show-flag.success',
					messageType: 'transactional',
					type: 'success',
					title: value ? successTitleOptingOutMessage : successTitleOptingInMessage,
				});

				if (value) {
					openSmartFieldsFeedbackCollector({
						feedbackTitle: formatMessage(messages.feedbackOptOutTitle),
						feedbackContext: [
							{
								id: 'smart-fields-opt-out',
								value: 'opted-out',
							},
						],
					});
				}
			} catch (err) {
				updateResource(() => ({ hasUserOptedOut, hasUserDismissedReadMore }));
				const actions = [
					{
						content: messages.refreshPageBtn,
						onClick: () => {
							// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
							window.location.reload();
						},
					},
				];
				showFlag({
					messageId: 'personal-settings-gic-smart-fields.ui.preference.show-flag.error',
					messageType: 'transactional',
					type: 'error',
					title: value ? messages.errTitleOptingOut : messages.errTitleOptingIn,
					description: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.errDescriptionOptingInIssueTermRefresh
						: messages.errDescriptionOptingIn,
					actions,
				});
			}
		},
		[
			hasUserDismissedReadMore,
			hasUserOptedOut,
			updateResource,
			updatePreferences,
			showFlag,
			openSmartFieldsFeedbackCollector,
			formatMessage,
			successTitleOptingInMessage,
			successTitleOptingOutMessage,
		],
	);

	if (loading) return null;

	return (
		<Toggle
			isChecked={!hasUserOptedOut}
			onChange={onChange}
			label={formatMessage(messages.toggleAriaLabel)}
		/>
	);
});
